import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useProduct, useCreateProduct } from '../../hooks/useProducts';
import { ProductForm } from '@modules/products';
import { PRODUCT_DESCRIPTION_MAX_LENGTH } from '../../utils/productValidation';
import { Modal } from '@shared/ui';
import { useCategories } from '@modules/categories';
import type { CategorySelectionItem } from '../CategorySelection';
import { Loader } from '@mantine/core';
import './CreateProductModal.css';

interface CreateProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    variantOfProductId?: number;
    categoryId?: number;
    sectionId?: number;
    onSuccess?: (productId?: number) => void;
}

export default function CreateProductModal({
    isOpen,
    onClose,
    variantOfProductId,
    categoryId,
    sectionId,
    onSuccess,
}: CreateProductModalProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: categories = [] } = useCategories();
    const { data: variantOfProduct, isLoading: variantLoading } = useProduct(variantOfProductId!);
    const createProduct = useCreateProduct();

    // Преобразуем данные продукта в initialValues для формы
    const initialValues = useMemo(() => {
        if (variantOfProduct) {
            // Преобразуем categories в categorySelections
            const categorySelections: CategorySelectionItem[] = variantOfProduct.categories && variantOfProduct.categories.length > 0
                ? variantOfProduct.categories.map((pc) => {
                    const category = pc.category as any; // Category from products/types may have sections
                    const sectionId = category?.sectionId
                        || category?.sections?.[0]?.sectionId
                        || '';
                    return {
                        sectionId: sectionId as number | '',
                        categoryId: pc.categoryId as number | '',
                    };
                })
                : [{ sectionId: '' as const, categoryId: '' as const }];



            return {
                categorySelections,
                name: variantOfProduct.name || '',
                description: (variantOfProduct.description || '').slice(0, PRODUCT_DESCRIPTION_MAX_LENGTH),
                price: variantOfProduct.price,
                composition: Array.isArray(variantOfProduct.composition) && variantOfProduct.composition.length > 0
                    ? variantOfProduct.composition
                    : [{ value: '', count: 0 }],
                keywords: (variantOfProduct.keywords || []).slice(0, 20),
                isNotFlower: variantOfProduct.isNotFlower ?? false,
                height: variantOfProduct.height || undefined,
                width: variantOfProduct.width || undefined,
                weight: variantOfProduct.weight || undefined,
                flowerCount: variantOfProduct.flowerCount || undefined,
                flowerTypeIds: variantOfProduct.flowerTypes
                    ? variantOfProduct.flowerTypes.map(pft => pft.flowerTypeId)
                    : variantOfProduct.flowerTypeId
                        ? [variantOfProduct.flowerTypeId]
                        : [],
                assemblyTypeId: variantOfProduct.assemblyTypeId ?? null,
                assemblyTimeMinutes: variantOfProduct.assemblyTimeMinutes ?? undefined,
                colorIds: variantOfProduct.colors
                    ? variantOfProduct.colors.map(pc => pc.colorId)
                    : variantOfProduct.colorId
                        ? [variantOfProduct.colorId]
                        : [],
                images: [],
            };
        }

        // Если передан categoryId, предзаполняем категорию
        if (categoryId) {
            const category = categories.find(c => c.id === categoryId);
            if (category) {
                // Используем sectionId из пропсов, если передан, иначе из категории
                const finalSectionId = sectionId || category.sectionId || '';
                return {
                    isNotFlower: false,
                    categorySelections: [{
                        sectionId: finalSectionId as number | '',
                        categoryId: categoryId as number | '',

                    }],
                };
            }
        }

        // Если передан только sectionId (без categoryId), создаем пустой элемент с sectionId
        if (sectionId) {
            return {
                isNotFlower: false,
                categorySelections: [{
                    sectionId: sectionId as number | '',
                    categoryId: '' as const,

                }],
            };
        }

        return undefined;
    }, [variantOfProduct, categories, categoryId, sectionId]);

    const handleSubmit = async (data: any) => {
        try {
            // Преобразуем categorySelections в categoryIds
            const categoryIds = data.categorySelections
                ?.filter((selection: any) => selection.categoryId && selection.categoryId !== '')
                .map((selection: any) => Number(selection.categoryId)) || [];

            // Преобразуем массивы ID в числа (могут быть строками или числами)
            const toNumberArray = (arr: any[] | undefined): number[] | undefined => {
                if (!arr || arr.length === 0) return undefined;
                return arr.map((id) => typeof id === 'string' ? Number(id) : id).filter((id) => !isNaN(id));
            };

            // Подготавливаем данные для создания товара
            const createData = {
                name: data.name,
                description: data.description,
                price: Number(data.price),
                categoryIds,
                isNotFlower: Boolean(data.isNotFlower ?? false),
                composition: data.composition?.filter((item: any) => item?.value?.trim()).length
                    ? data.composition.filter((item: any) => item?.value?.trim()).map((item: any) => ({ value: String(item.value).trim(), count: Number(item.count) || 0 }))
                    : undefined,
                keywords: data.keywords && data.keywords.length > 0 ? data.keywords : undefined,
                height: data.height ? Number(data.height) : undefined,
                width: data.width ? Number(data.width) : undefined,
                weight: data.weight ? Number(data.weight) : undefined,
                flowerCount: data.flowerCount ? Number(data.flowerCount) : undefined,
                flowerTypeIds: toNumberArray(data.flowerTypeIds),
                assemblyTypeId: data.assemblyTypeId && data.assemblyTypeId !== null ? Number(data.assemblyTypeId) : undefined,
                assemblyTimeMinutes: data.assemblyTimeMinutes != null && data.assemblyTimeMinutes !== '' && Number(data.assemblyTimeMinutes) > 0
                    ? Number(data.assemblyTimeMinutes)
                    : undefined,
                colorIds: toNumberArray(data.colorIds),
                variantInfo: variantOfProductId ? {
                    variantOfProductId: variantOfProductId,
                } : undefined,
            };

            // Извлекаем файлы изображений (может быть File, File[] или null)
            let files: File[] | undefined;
            if (data.images) {
                if (Array.isArray(data.images)) {
                    files = data.images.length > 0 ? data.images : undefined;
                } else if (data.images instanceof File) {
                    files = [data.images];
                }
            }

            // Вызываем мутацию для создания товара
            const createdProduct = await createProduct.mutateAsync({ data: createData, files });

            // Показываем уведомление об успехе
            notifications.show({
                title: 'Успешно',
                message: variantOfProductId
                    ? 'Вариант товара успешно создан'
                    : 'Товар успешно создан',
                color: 'green',
            });

            // Закрываем модалку
            onClose();
            onSuccess?.(createdProduct.id);
            navigate(`/products/${createdProduct.id}`, {
                state: {
                    from: location.pathname,
                    openStocksModalOnLoad: true,
                    showModerationInfoAfterStocks: true,
                    ...(variantOfProductId ? { openVariantsModalOnLoad: true } : {}),
                },
            });
        } catch (error: any) {
            // Получаем сообщение об ошибке
            const errorMessage = error?.response?.data?.message
                || error?.message
                || 'Произошла ошибка при создании товара';

            // Показываем уведомление об ошибке
            notifications.show({
                title: 'Ошибка',
                message: errorMessage,
                color: 'red',
            });
        }
    };

    const modalTitle = variantOfProductId && variantOfProduct
        ? `Добавить товар (вариант: ${variantOfProduct.name})`
        : 'Добавить товар';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            maxWidth={1200}
        >
            <div className="create-product-modal-content">
                {((variantOfProductId && variantLoading)) ? (
                    <div className="empty-container" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                        <Loader />
                    </div>
                ) : (
                    (!variantOfProductId || variantOfProduct) && (
                        <ProductForm
                            onSubmit={handleSubmit}
                            initialValues={initialValues}
                            isLoading={createProduct.isPending}
                        />
                    )
                )}
            </div>
        </Modal>
    );
}

